import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import "../style/StoreLocator.css";

const containerStyle = {
  width: '100%',
  height: '500px'
};

// <-- Replace with your backend base if it changes later -->
const API_BASE = "https://ad-backend-7gm3.onrender.com";

const StoreLocator = () => {
  const [stores, setStores] = useState([]);
  const [userLocation, setUserLocation] = useState(null); // reference location (GPS or PIN)
  const [mapCenter, setMapCenter] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [searchPincode, setSearchPincode] = useState('');
  const [carouselIndex, setCarouselIndex] = useState(0);

  const [geoTried, setGeoTried] = useState(false);   // whether geolocation was attempted
  const [askingPin, setAskingPin] = useState(false); // show PIN UI if gps denied or unavailable
  const [loadingPin, setLoadingPin] = useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  });

  // ---------------------- FETCH BRANCHES ----------------------
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'branches'));
        const storeList = [];
        querySnapshot.forEach((doc) => {
          storeList.push({ id: doc.id, ...doc.data() });
        });
        setStores(storeList);
      } catch (error) {
        console.error('Error fetching stores:', error);
      }
    };

    fetchStores();
  }, []);

  // ---------------------- TRY GPS ON LOAD ----------------------
  useEffect(() => {
    if (!navigator || !navigator.geolocation) {
      setGeoTried(true);
      setAskingPin(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
        setUserLocation(loc);
        setMapCenter(loc);
        setGeoTried(true);
      },
      (error) => {
        console.warn('Geolocation not available or denied:', error);
        setGeoTried(true);
        setAskingPin(true);
      },
      { timeout: 10000 }
    );
  }, []);

  // ensure mapCenter follows userLocation when userLocation changes (but don't overwrite a manual center)
  useEffect(() => {
    if (userLocation && !mapCenter) {
      setMapCenter(userLocation);
    }
  }, [userLocation, mapCenter]);

  // ---------------------- CAROUSEL ----------------------
  useEffect(() => {
    if (selectedStore) {
      const imgs = [
        ...(selectedStore.insideImages || []),
        ...(selectedStore.outsideImages || [])
      ];
      if (imgs.length > 1) {
        const t = setInterval(() => {
          setCarouselIndex((prev) => (prev + 1) % imgs.length);
        }, 2000);
        return () => clearInterval(t);
      }
    }
  }, [selectedStore]);

  // ---------------------- BACKEND GET /geocode/:pincode ----------------------
  const fetchLatLngFromApi = async (pin) => {
    if (!/^\s*\d{6}\s*$/.test(pin)) {
      throw new Error('Enter a valid 6-digit pincode');
    }
    setLoadingPin(true);
    try {
      const url = `${API_BASE}/geocode/${pin.trim()}`;
      const res = await fetch(url);
      if (!res.ok) {
        // try to parse JSON error
        let errText = `${res.status} ${res.statusText}`;
        try {
          const j = await res.json();
          if (j && j.error) errText = j.error;
        } catch (_) {}
        throw new Error(errText);
      }
      const data = await res.json();
      if (!data.ok || typeof data.lat !== 'number' || typeof data.lng !== 'number') {
        throw new Error(data.error || 'Invalid geocode response');
      }
      return { lat: data.lat, lng: data.lng };
    } finally {
      setLoadingPin(false);
    }
  };

  // ---------------------- PIN SEARCH HANDLER (top box or after deny) ----------------------
  const handlePincodeSearch = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const pin = searchPincode.trim();
    if (!/^\d{6}$/.test(pin)) {
      alert('Please enter a valid 6-digit PIN code');
      return;
    }

    try {
      const loc = await fetchLatLngFromApi(pin);
      setUserLocation(loc);
      setMapCenter(loc);
      setAskingPin(false);
    } catch (err) {
      console.error('Geocode error:', err);
      alert(err.message || 'Failed to geocode pincode');
    }
  };

  // ---------------------- DISTANCE (Haversine) ----------------------
  const getDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // ---------------------- SORT STORES BY DISTANCE BASED ON referenceLocation ----------------------
  const referenceLocation = userLocation || mapCenter || null;
  let sortedStores = [...stores];
  if (referenceLocation) {
    sortedStores = stores
      .map((store) => {
        const sLat = store.location?.lat;
        const sLng = store.location?.long ?? store.location?.lng;
        let distance = null;
        if (typeof sLat === 'number' && typeof sLng === 'number') {
          distance = getDistance(referenceLocation.lat, referenceLocation.lng, sLat, sLng);
        }
        return { ...store, distance: distance === null ? null : distance.toFixed(2) };
      })
      .sort((a, b) => {
        if (a.distance === null) return 1;
        if (b.distance === null) return -1;
        return a.distance - b.distance;
      });
  }

  // ---------------------- UI: ask for PIN if GPS not available ----------------------
  if (geoTried && !referenceLocation && askingPin) {
    return (
      <div className="store-locator-container">
        <h2>Store Locator</h2>

        <div className="store-locator-controls">
          <form onSubmit={handlePincodeSearch} className="pincode-search">
            <input
              type="text"
              placeholder="Enter Pincode (6 digits)"
              value={searchPincode}
              onChange={(e) => setSearchPincode(e.target.value)}
            />
            <button type="submit" disabled={loadingPin}>
              {loadingPin ? 'Searching...' : 'Search'}
            </button>
          </form>
        </div>

        <div style={{ marginTop: 16 }}>
          <p>
            We couldn't get your device location. Enter your PIN code to find nearby stores,
            or allow location access in your browser and refresh the page.
          </p>
        </div>
      </div>
    );
  }

  if (!isLoaded || !mapCenter) {
    return <div>Loading map...</div>;
  }

  // ---------------------- NORMAL UI (map + list) ----------------------
  return (
    <div className="store-locator-container">
      <h2>Store Locator</h2>

      <div className="store-locator-controls">
        <form onSubmit={handlePincodeSearch} className="pincode-search">
          <input
            type="text"
            placeholder="Enter Pincode"
            value={searchPincode}
            onChange={(e) => setSearchPincode(e.target.value)}
          />
          <button type="submit" disabled={loadingPin}>
            {loadingPin ? 'Searching...' : 'Search'}
          </button>
        </form>
      </div>

      <div className="store-locator-layout">
        <div className="store-list">
          <h3>Nearby Stores</h3>
          {sortedStores.length === 0 && <p>No stores found.</p>}
          {sortedStores.map((store) => (
            <div
              key={store.id}
              className="store-list-item"
              onClick={() => {
                const lat = store.location?.lat;
                const lng = store.location?.long ?? store.location?.lng;
                if (typeof lat === 'number' && typeof lng === 'number') {
                  setMapCenter({ lat, lng });
                  setSelectedStore(store);
                  setCarouselIndex(0);
                }
              }}
            >
              <strong>{store.storeName}</strong>
              {store.distance && <p>{store.distance} km away</p>}
              <p>{store.pincode || ''}</p>
            </div>
          ))}
        </div>

        <div className="store-map">
          <GoogleMap mapContainerStyle={containerStyle} center={mapCenter} zoom={12}>
            {stores.map((store) => {
              const lat = store.location?.lat;
              const lng = store.location?.long ?? store.location?.lng;
              if (typeof lat !== 'number' || typeof lng !== 'number') return null;
              return (
                <Marker
                  key={store.id}
                  position={{ lat, lng }}
                  onClick={() => {
                    setSelectedStore(store);
                    setCarouselIndex(0);
                    setMapCenter({ lat, lng });
                  }}
                />
              );
            })}

            {selectedStore && (
              <InfoWindow
                position={{
                  lat: selectedStore.location.lat,
                  lng: selectedStore.location.long
                }}
                onCloseClick={() => setSelectedStore(null)}
              >
                <div className="store-popup">
                  <h3>{selectedStore.storeName}</h3>

                  <div className="store-popup-content">
                    <div className="popup-carousel">
                      {(() => {
                        const imgs = [
                          ...(selectedStore.insideImages || []),
                          ...(selectedStore.outsideImages || [])
                        ];
                        return imgs.length ? (
                          <img src={imgs[carouselIndex]} alt={`Store ${carouselIndex}`} className="carousel-image" />
                        ) : (
                          <p>No images available</p>
                        );
                      })()}
                    </div>

                    <div className="popup-details">
                      <p>{selectedStore.storePhone}</p>
                      <p>{selectedStore.storeEmail}</p>
                      <p>{selectedStore.managerName}</p>
                      <p>{selectedStore.managerPhone}</p>
                      <p>{selectedStore.managerEmail}</p>

                      <button
                        onClick={() =>
                          window.open(
                            `https://www.google.com/maps/dir/?api=1&destination=${selectedStore.location.lat},${selectedStore.location.long}`,
                            '_blank'
                          )
                        }
                      >
                        Open Directions
                      </button>
                    </div>
                  </div>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </div>
      </div>
    </div>
  );
};

export default StoreLocator;
